/* eslint-disable react/no-danger */
import { useMemo } from "react";
import { Box, Divider, Image, Text } from "@mantine/core";
import type {
  ReportAmendmentNoticeFragmentConfig,
  ReportSupplementEntry,
} from "../../types/reportTemplate";
import { CLINICAL_RICH_TEXT_CLASS } from "../../utils/clinicalRichText";
import { isEmptyHtml, resolveRichTextForDisplay } from "../../utils/helpers";
import { LAB_TIME_ZONE } from "../../utils/labTime";

interface ReportAmendmentEntryProps {
  entry: ReportSupplementEntry;
  showDivider: boolean;
  fragment?: ReportAmendmentNoticeFragmentConfig;
}

function formatSupplementDate(dateValue: string): string {
  const date = new Date(dateValue);
  const calendarDate = date.toLocaleDateString("en-US", {
    timeZone: LAB_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const clockTime = date.toLocaleTimeString("en-US", {
    timeZone: LAB_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${calendarDate} ${clockTime}`;
}

export function ReportAmendmentEntry({
  entry,
  showDivider,
  fragment,
}: ReportAmendmentEntryProps) {
  const content = useMemo(
    () =>
      fragment?.contentHtml ??
      resolveRichTextForDisplay(
        entry.contentHtml || entry.comments || entry.amendmentReason || "",
        {}
      ),
    [entry.amendmentReason, entry.comments, entry.contentHtml, fragment]
  );
  const signedAt = useMemo(
    () => formatSupplementDate(entry.signedAt),
    [entry.signedAt]
  );
  const showReasonHeading = useMemo(
    () => !isEmptyHtml(content) && (fragment?.showReasonHeading ?? !fragment),
    [content, fragment]
  );

  return (
    <Box
      data-report-amendment-entry={entry.id}
      style={{ breakInside: "avoid", pageBreakInside: "avoid" }}
    >
      {showDivider && <Divider my="8pt" />}
      {showReasonHeading && (
        <Text
          data-report-amendment-reason-heading="true"
          ta="center"
          fw={700}
          mt="8pt"
          style={{
            fontSize: "11pt",
            lineHeight: 1.15,
            textDecoration: "underline",
            breakAfter: "avoid",
            pageBreakAfter: "avoid",
          }}
        >
          Amendment Reason
        </Text>
      )}
      {content && (
        <Box
          className={CLINICAL_RICH_TEXT_CLASS}
          mt="8pt"
          style={{ fontSize: "11pt", lineHeight: 1.15 }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
      {(fragment?.showAttribution ?? true) && (
        <Box
          data-report-amendment-attribution="true"
          mt="8pt"
          ml="auto"
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto",
            columnGap: "8pt",
            justifyContent: "end",
            alignItems: "start",
            maxWidth: "100%",
            textAlign: "right",
          }}
        >
          <Text fw={700} size="sm" style={{ letterSpacing: 1 }}>
            ELECTRONICALLY SIGNED BY:
          </Text>
          <Box style={{ textAlign: "right" }}>
            {entry.signatureImageDataUri && (
              <Image
                src={entry.signatureImageDataUri}
                alt={`${entry.signedBy} signature`}
                fit="contain"
                style={{
                  height: "33.4pt",
                  width: "auto",
                  maxWidth: "185.5pt",
                  display: "block",
                  margin: "0 0 0 auto",
                }}
              />
            )}
            <Text size="sm" fw={600}>
              {entry.signedBy}
            </Text>
            <Text size="sm">{signedAt}</Text>
          </Box>
        </Box>
      )}
    </Box>
  );
}
